import { api } from '@lcdev/fetch';
import { structuredMapper } from '@lcdev/mapper';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { Json } from '@lcdev/ts';
import { parseISO } from 'date-fns';

export type StoredProcedureParameters = {
  [name: string]: string;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
namespace Api {
  /* eslint-disable camelcase */

  export type PlayerStanding = {
    pk_player_id: number;
    player_name: string;
    elo_score: number;
    rankChange: string;
  };

  export type PlayerPicture = {
    pk_player_id: number;
    player_pic: string;
  };

  export type MatchDetail = {
    matchGUID?: string;
    matchDatetime: string;

    p1PkPlayerId: number;
    p1Name: string;
    p1Score: number;
    p1WinsLast30Days: number;
    p1BeatP2: number;
    p1TotalWins: number;
    p1TotalGames: number;

    p2PkPlayerId: number;
    p2Name: string;
    p2Score: number;
    p2WinsLast30Days: number;
    p2BeatP1: number;
    p2TotalWins: number;
    p2TotalGames: number;
  };

  export type Match = {
    matchGUID: string;
    timestamp: number;
    url: string;
  };

  /* eslint-enable camelcase */
}

export type PlayerStanding = {
  id: number;
  name: string;
  eloScore: number;
};

export type PlayerPhoto = {
  id: number;
  url: string;
};

export type Match = {
  id: string;
  datetime: Date;
  videoUrl: string;
};

export type MatchPlayer = {
  id: number;
  name: string;
  score: number;
  winsLast30Days: number;
  winsAgainstOpponent: number;
  winsTotal: number;
  gamesTotal: number;
};

export type MatchDetail = {
  id: string;
  datetime: Date;
  player1: MatchPlayer;
  player2: MatchPlayer;
};

const statsApi = api('https://stats.patronscan.com');

function callStoredProcedure<T extends Json>(
  name: string,
  parameters: StoredProcedureParameters = {},
) {
  return statsApi
    .get('/squash')
    .withQuery({
      token: '42146619-9d2a-433b-8a72-4861b6994d11',
      sp: name,
      ...parameters,
    })
    .json<T>();
}

export async function getPlayerStandings() {
  const apiPlayerStandings = await callStoredProcedure<Api.PlayerStanding[]>('spGetPlayersElo');
  return apiPlayerStandings.map((playerStanding) =>
    structuredMapper<Api.PlayerStanding, PlayerStanding>(playerStanding, {
      pk_player_id: {
        map: (value: number) => value,
        rename: 'id',
      },
      player_name: {
        map: (value: string) => value,
        rename: 'name',
      },
      elo_score: {
        map: (value: number) => value,
        rename: 'eloScore',
      },
    }),
  );
}

export async function getPlayerPhotos() {
  const apiPlayerPictures = await callStoredProcedure<Api.PlayerPicture[]>('spGetPlayersPics');
  return Promise.all(
    apiPlayerPictures.map<Promise<PlayerPhoto>>(async (playerPicture) => {
      const base64Response = await fetch(`data:image/jpeg;base64,${playerPicture.player_pic}`);
      const blob = await base64Response.blob();

      return {
        id: playerPicture.pk_player_id,
        url: URL.createObjectURL(blob),
      };
    }),
  );
}

export async function getMatchDetail(matchId: string): Promise<MatchDetail | undefined> {
  const apiMatchDetail = await callStoredProcedure<Api.MatchDetail>('spGetVideoDetails', {
    piFilename: matchId,
  });

  // Some match IDs don't return valid match details
  // Return undefined if the match isn't found, or match datetime is null
  if (!apiMatchDetail?.matchDatetime) {
    return undefined;
  }

  const matchDetail: MatchDetail = {
    ...structuredMapper(apiMatchDetail, {
      matchDatetime: {
        map: (value: string) => parseISO(value),
        rename: 'datetime',
      },
    }),
    id: matchId,
    player1: structuredMapper(apiMatchDetail, {
      p1PkPlayerId: {
        map: (value: number) => value,
        rename: 'id',
      },
      p1Name: {
        map: (value: string) => value,
        rename: 'name',
      },
      p1Score: {
        map: (value: number) => value,
        rename: 'score',
      },
      p1WinsLast30Days: {
        map: (value: number) => value,
        rename: 'winsLast30Days',
      },
      p1BeatP2: {
        map: (value: number) => value,
        rename: 'winsAgainstOpponent',
      },
      p1TotalWins: {
        map: (value: number) => value,
        rename: 'winsTotal',
      },
      p1TotalGames: {
        map: (value: number) => value,
        rename: 'gamesTotal',
      },
    }),
    player2: structuredMapper(apiMatchDetail, {
      p2PkPlayerId: {
        map: (value: number) => value,
        rename: 'id',
      },
      p2Name: {
        map: (value: string) => value,
        rename: 'name',
      },
      p2Score: {
        map: (value: number) => value,
        rename: 'score',
      },
      p2WinsLast30Days: {
        map: (value: number) => value,
        rename: 'winsLast30Days',
      },
      p2BeatP1: {
        map: (value: number) => value,
        rename: 'winsAgainstOpponent',
      },
      p2TotalWins: {
        map: (value: number) => value,
        rename: 'winsTotal',
      },
      p2TotalGames: {
        map: (value: number) => value,
        rename: 'gamesTotal',
      },
    }),
  };

  return matchDetail;
}

export async function getMatches() {
  const matches = await statsApi
    .get('/video-list')
    .withQuery({
      token: '42146619-9d2a-433b-8a72-4861b6994d11',
    })
    .json<Api.Match[]>();

  return matches.map((match) =>
    structuredMapper<Api.Match, Match>(match, {
      matchGUID: {
        map: (value: string) => value,
        rename: 'id',
      },
      timestamp: {
        map: (value: number) => new Date(value),
        rename: 'datetime',
      },
      url: {
        map: (value: string) => value,
        rename: 'videoUrl',
      },
    }),
  );
}
